import { Icon } from '@iconify/react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CopiarLink } from '../components/CopiarLink';
import { MenuContexto, type ItemMenu } from '../components/MenuContexto';
import { api } from '../lib/api';
import { Barra, Botao, Carregando, ErroInline, TagComPonto } from '../components/Sga';
import {
  iconeDoCanal,
  iconeDoPerfilAntiban,
  kindDoStatus,
  n,
  rotuloStatus,
  segundos,
  identidadeDaConexao,
} from '../lib/format';
import type { Conexao, LinkFormulario } from '../lib/types';

export function Conexoes({
  conexoes,
  carregando,
  erro,
  aoRecarregar,
  busca,
  aoBuscar,
  selecionada,
  aoSelecionar,
  aoEditar,
  aoReemparelhar,
  aoExcluir,
  aoNova,
  podeEscrever,
}: {
  conexoes: Conexao[];
  carregando: boolean;
  erro: string | null;
  aoRecarregar: () => void;
  tenantNome: string;
  busca: string;
  aoBuscar: (v: string) => void;
  selecionada: string | null;
  aoSelecionar: (id: string) => void;
  aoEditar: (id: string) => void;
  aoReemparelhar: (id: string) => void;
  aoExcluir: (c: Conexao) => void;
  aoNova: () => void;
  podeEscrever: boolean;
}) {
  const [filtro, setFiltro] = useState('');
  const termo = (busca || filtro).trim().toLowerCase();

  /**
   * Link de suporte por conexão, para o botão de copiar.
   *
   * Vem do gateway em vez de ser montado aqui: só ele conhece o token do link
   * e a base pública. Montar `origem + '/c/' + token` no navegador daria o
   * endereço do painel, que não é o mesmo que atende a página de suporte
   * quando há um domínio próprio para ela.
   *
   * Falhar aqui esconde o botão e não quebra a tela — a grade de conexões
   * continua sendo a grade de conexões sem ele.
   */
  const [links, setLinks] = useState<Record<string, string>>({});
  useEffect(() => {
    let vivo = true;
    api
      .get<{ filas: { conexao_id: string; link: LinkFormulario | null }[] }>('/links-fila')
      .then((d) => {
        if (!vivo) return;
        const mapa: Record<string, string> = {};
        for (const f of d.filas) if (f.link?.ativo && f.link.url) mapa[f.conexao_id] = f.link.url;
        setLinks(mapa);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [conexoes]);

  /**
   * Menu de contexto aberto: posição do cursor e, se houver, a conexão sob ele.
   *
   * `conexao: null` é o clique no vazio da tabela — ali só cabem as ações
   * gerais, porque não há linha sobre a qual editar ou excluir.
   */
  const [menu, setMenu] = useState<{ x: number; y: number; conexao: Conexao | null } | null>(null);
  const fecharMenu = useCallback(() => setMenu(null), []);

  /**
   * Abre o menu na posição do cursor e seleciona a linha, para ficar claro
   * sobre qual conexão as ações vão agir.
   *
   * `preventDefault` só aqui: suprimir o menu do navegador na página inteira
   * tiraria copiar/colar e inspecionar do resto do painel.
   */
  const abrirMenu = (e: React.MouseEvent, conexao: Conexao) => {
    e.preventDefault();
    aoSelecionar(conexao.id);
    setMenu({ x: e.clientX, y: e.clientY, conexao });
  };

  /**
   * Clique direito fora de qualquer linha.
   *
   * A checagem do alvo importa: sem ela, o clique direito numa linha subiria
   * por borbulhamento e o menu geral substituiria o da conexão logo depois de
   * ele abrir.
   */
  const abrirMenuGeral = (e: React.MouseEvent) => {
    // `tr[data-conexao]` e nao `tr`: a linha de "nenhuma conexao" tambem vive
    // no tbody, entao o seletor generico engolia justamente o clique que a
    // mensagem pede ("Comece por Nova conexao"). O menu so abria no cabecalho.
    if ((e.target as HTMLElement).closest('tbody tr[data-conexao]')) return;
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, conexao: null });
  };

  /** Ações que não dependem de linha nenhuma — valem no vazio da tabela. */
  const itensGerais = (): ItemMenu[] => [
    {
      rotulo: 'Nova conexão',
      icone: 'tdesign:file-add',
      onClick: aoNova,
      desabilitado: !podeEscrever,
      motivoBloqueio: 'Seu perfil é somente leitura',
    },
  ];

  const itensDoMenu = (c: Conexao): ItemMenu[] => [
    ...itensGerais(),
    {
      rotulo: 'Editar cadastro',
      separadorAntes: true,
      icone: 'uil:edit',
      onClick: () => aoEditar(c.id),
    },
    {
      rotulo: c.usa_qr ? 'Reemparelhar com QR Code' : 'Revalidar credenciais',
      icone: c.usa_qr ? 'mdi:qrcode' : 'mdi:check-decagram',
      onClick: () => aoReemparelhar(c.id),
      desabilitado: !podeEscrever,
      motivoBloqueio: 'Seu perfil é somente leitura',
    },
    {
      rotulo: 'Excluir conexão',
      icone: 'uil:trash',
      onClick: () => aoExcluir(c),
      desabilitado: !podeEscrever,
      motivoBloqueio: 'Seu perfil é somente leitura',
      perigo: true,
      separadorAntes: true,
    },
  ];

  const filtradas = useMemo(() => {
    if (!termo) return conexoes;
    return conexoes.filter((c) =>
      [c.nome, c.numero ?? '', c.setor ?? '', c.canal_label, rotuloStatus[c.status]]
        .join(' ')
        .toLowerCase()
        .includes(termo),
    );
  }, [conexoes, termo]);

  // A barra de fila é proporcional à maior fila da lista, como no protótipo.
  const maiorFila = Math.max(1, ...conexoes.map((c) => c.na_fila));

  if (erro) return <ErroInline mensagem={erro} aoTentar={aoRecarregar} />;

  return (
    <>
      {/* A faixa de ações saiu: dentro do SGA Desk ela era a quinta barra
          empilhada antes do conteúdo, e três dos quatro controles ou
          duplicavam algo ou não faziam nada — "Filtros avançados" só limpava
          o campo, e "Resetar filtros" fazia o mesmo.

          "Nova conexão" foi para o clique direito, inclusive no vazio da
          tabela. A busca desceu para o cabeçalho da coluna: ela não podia
          sumir junto, porque a busca global do painel também não é desenhada
          quando estamos embutidos. */}
      <div className="sga-tabela-scroll" onContextMenu={abrirMenuGeral}>
        {/* `minWidth` mantem as colunas legiveis enquanto a tabela e tabela.
            Abaixo de 900px o CSS troca para cartoes e neutraliza este valor —
            senao ele forcaria rolagem lateral justamente onde nao ha espaco. */}
        <table className="sga-table sga-table--adaptavel" style={{ tableLayout: 'fixed', minWidth: 1180 }}>
          <thead>
            <tr>
              <th style={{ width: 260 }}>
                <div className="th-busca">
                  <Icon icon="mdi:magnify" width={14} />
                  <input
                    value={filtro}
                    placeholder="Conexão"
                    aria-label="Filtrar conexões"
                    onChange={(e) => {
                      setFiltro(e.target.value);
                      aoBuscar('');
                    }}
                  />
                  {termo && (
                    <button
                      type="button"
                      onClick={() => {
                        setFiltro('');
                        aoBuscar('');
                      }}
                      title="Limpar filtro"
                      aria-label="Limpar filtro"
                    >
                      <Icon icon="mdi:close" width={13} />
                    </button>
                  )}
                </div>
              </th>
              <th style={{ width: 180 }}>Número autenticado</th>
              <th style={{ width: 150 }}>Status</th>
              <th style={{ width: 140 }}>Fila</th>
              <th style={{ width: 110, textAlign: 'right' }}>Enviadas 24h</th>
              <th style={{ width: 100, textAlign: 'right' }}>Delay médio</th>
              <th style={{ width: 190 }}>Anti-Ban</th>
              <th style={{ width: 60, textAlign: 'right' }}>
                {/* Com a faixa de acoes removida no embed, este e o unico
                    ponto sempre visivel para criar. O clique direito continua
                    valendo — isto e a alternativa descobrivel. */}
                <button
                  type="button"
                  className="th-acao-nova"
                  onClick={aoNova}
                  disabled={!podeEscrever}
                  title={podeEscrever ? 'Nova conexão' : 'Seu perfil é somente leitura'}
                  aria-label="Nova conexão"
                >
                  <Icon icon="mdi:plus" width={14} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {carregando && conexoes.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <Carregando texto="Carregando conexões…" />
                </td>
              </tr>
            )}
            {!carregando && filtradas.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="sga-vazio">
                    <Icon icon="mdi:qrcode-remove" width={24} />
                    {termo ? (
                      'Nenhuma conexão bate com a busca.'
                    ) : (
                      <>
                        Nenhuma conexão neste tenant.
                        {/* Botao de verdade, nao so a instrucao: a acao morava
                            apenas no clique direito, e quem chega na tela vazia
                            procura um botao que nao existia. */}
                        <Botao
                          variante="primary"
                          icone="tdesign:file-add"
                          onClick={aoNova}
                          disabled={!podeEscrever}
                          title={podeEscrever ? undefined : 'Seu perfil é somente leitura'}
                          style={{ marginLeft: 10 }}
                        >
                          Nova conexão
                        </Botao>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {filtradas.map((c) => {
              const kind = kindDoStatus(c.status);
              const canal = iconeDoCanal[c.canal];
              const perfil = c.perfil_antiban
                ? (c.perfil_antiban.toLowerCase().includes('rígido') ||
                   c.perfil_antiban.toLowerCase().includes('rigido')
                    ? 'rigido'
                    : c.perfil_antiban.toLowerCase().includes('aquec')
                      ? 'aquecimento'
                      : c.perfil_antiban.toLowerCase().includes('aplica')
                        ? 'nao_aplica'
                        : 'padrao')
                : 'nao_aplica';

              return (
                <tr
                  key={c.id}
                  data-conexao={c.id}
                  className={selecionada === c.id ? 'focused' : undefined}
                  tabIndex={0}
                  onClick={() => aoSelecionar(c.id)}
                  onContextMenu={(e) => abrirMenu(e, c)}
                  onKeyDown={(e) => {
                    // Teclado: Enter seleciona, Space abre o menu de ações no
                    // canto da linha — sem mouse a grade continua operável.
                    if (e.key === 'Enter') {
                      aoSelecionar(c.id);
                    } else if (e.key === ' ') {
                      e.preventDefault();
                      const r = e.currentTarget.getBoundingClientRect();
                      aoSelecionar(c.id);
                      setMenu({ x: r.left + 160, y: r.top + 30, conexao: c });
                    }
                  }}
                >
                  <td>
                    <div className="sga-conexao">
                      <span
                        className="sga-avatar-canal"
                        style={{
                          background:
                            kind === 'err'
                              ? 'rgba(255,77,79,.1)'
                              : kind === 'warn'
                                ? 'rgba(230,145,56,.14)'
                                : kind === 'mut'
                                  ? 'var(--sga-gray-2)'
                                  : 'var(--sga-tag-bg)',
                        }}
                      >
                        <Icon icon={canal.icone} width={19} style={{ color: canal.cor }} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="sga-conexao__nome">{c.nome}</div>
                        <div className="sga-conexao__sub">
                          {c.canal_label} · {c.setor ?? 'sem setor'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="celula-estreita" data-rotulo="Número autenticado">
                    {/* `title` com o valor cru: truncar sem dar como ver o
                        conteúdo inteiro esconde informação em vez de organizar. */}
                    <div className="identidade-conexao">
                      <span className="mono truncado" style={{ fontSize: 13 }} title={c.numero ?? ''}>
                        {identidadeDaConexao(c.canal, c.numero)}
                      </span>
                      {/* Só onde há link de chat: no WhatsApp e no Telegram o
                          "número autenticado" não é endereço de nada. */}
                      {links[c.id] && <CopiarLink url={links[c.id]} />}
                    </div>
                    <div className="sga-conexao__sub">sessão {c.sessao_texto}</div>
                  </td>
                  <td data-rotulo="Status">
                    <TagComPonto kind={kind} grande>
                      {rotuloStatus[c.status]}
                    </TagComPonto>
                  </td>
                  <td data-rotulo="Fila">
                    <div style={{ fontSize: 13 }} data-zero={c.na_fila ? undefined : 'true'}>
                      {n(c.na_fila)}{' '}
                      <span style={{ color: 'var(--sga-fg-3-texto)', fontSize: 11.5 }}>na fila</span>
                    </div>
                    <Barra pct={(c.na_fila / maiorFila) * 100} kind={kind === 'mut' ? 'mut' : kind} />
                  </td>
                  <td className="num" data-rotulo="Enviadas 24h" data-zero={c.enviadas_24h ? undefined : 'true'}>
                    {n(c.enviadas_24h)}
                  </td>
                  <td className="num" data-rotulo="Delay médio" data-zero={c.delay_medio_ms ? undefined : 'true'}>
                    {segundos(c.delay_medio_ms)}
                  </td>
                  <td data-rotulo="Anti-Ban">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                      <Icon
                        icon={iconeDoPerfilAntiban[perfil]}
                        width={16}
                        style={{ color: perfil === 'rigido' ? 'var(--sga-warning)' : 'var(--sga-fg-2)' }}
                      />
                      {c.antiban_texto}
                    </span>
                  </td>
                  <td className="celula-acoes">
                    {/* As ações vivem no clique direito da linha. Este botão
                        abre o mesmo menu — sem ele, quem usa touch ou teclado
                        não teria como alcançar editar/excluir. */}
                    <div className="sga-table__acoes">
                      <button
                        type="button"
                        className="sga-table__acao"
                        title="Ações da conexão (ou clique direito na linha)"
                        aria-haspopup="menu"
                        onClick={(e) => {
                          e.stopPropagation();
                          const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          aoSelecionar(c.id);
                          setMenu({ x: r.right, y: r.bottom + 4, conexao: c });
                        }}
                      >
                        <Icon icon="mdi:dots-vertical" width={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div
        style={{
          display: 'flex',
          fontSize: 11.5,
          color: 'var(--sga-fg-3)',
          padding: '2px 6px 6px',
        }}
      >
        <span>
          {filtradas.length} conexões · seleção: {selecionada ? 1 : 0}
        </span>
        <span style={{ flex: 1 }} />
        <span>SGA Petro © 2011–2026. Todos os direitos reservados</span>
      </div>

      {menu && (
        <MenuContexto
          x={menu.x}
          y={menu.y}
          titulo={menu.conexao?.nome ?? 'Conexões'}
          itens={menu.conexao ? itensDoMenu(menu.conexao) : itensGerais()}
          aoFechar={fecharMenu}
        />
      )}
    </>
  );
}
